import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  formatAmountRaw as coreFormatAmountRaw,
  formatAmountPlainRaw,
  formatCompactRaw,
  formatRupeesRaw,
} from "@core";
import { maskMoney } from "./privacy/mask";
import { shouldHideValues } from "./privacy/store";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format paise as INR with Indian numbering — EXACT: ₹2,500.45, and ₹2,500
 * (no `.00`) only when the amount is whole. Never rounds paise away.
 *
 * HIDE-VALUES (V5-03) IS ENFORCED HERE, AND THAT IS ON PURPOSE
 * -------------------------------------------------------------
 * This function is called inline in JSX across ~71 files. There is no shared
 * `<Amount>` element wrapping them and never was, so the only layer that sees
 * every amount in the app is this one. Masking here means the global toggle
 * reaches every existing screen without editing any of them.
 *
 * Three properties keep that honest:
 *
 *  * It masks the STRING, never the number. `paise` is unchanged, so every
 *    total, chart series and request body is computed from the real value and a
 *    hidden figure is still the right figure.
 *  * `shouldHideValues()` is a synchronous module read, not a hook, because
 *    this is a plain function called from anywhere.
 *  * A re-render is required for the change to appear. `usePrivacyRevision()`
 *    in `App.tsx` provides it by re-rendering the route tree when the flag
 *    flips — see the comment there.
 *
 * `formatAmountRaw` below is the escape hatch for the places that must NOT be
 * masked (exports, print views, PDFs).
 */
export function formatINR(paise: number): string {
  const formatted = coreFormatAmountRaw(paise);
  return shouldHideValues() ? maskMoney(formatted) : formatted;
}

/**
 * The unmasked formatter.
 *
 * For deliberate EXPORTS — CSV, Excel, PDF, the printable rent statement. A
 * user who clicks "download" has asked for the numbers; handing them a file
 * full of bullets because a screen toggle happened to be on would be a bug,
 * not a privacy win. Also used by `<AmountText>`, which does its own masking
 * as markup.
 *
 * Re-exported from `@core` so the 71 existing call sites do not move.
 */
export const formatAmountRaw = coreFormatAmountRaw;

/** Exact paise with Indian grouping and no `₹` — `2,500.45`. Unmasked. */
export { formatAmountPlainRaw };

/**
 * Exact INR from a RUPEE number (a chart series value, a rupee-denominated
 * API field). Masked under hide-values. `2500.45` -> `₹2,500.45`.
 */
export function formatRupeesINR(rupees: number): string {
  const formatted = formatRupeesRaw(rupees);
  return shouldHideValues() ? maskMoney(formatted) : formatted;
}

/** Exact paise with no `₹` — `2,500.45`. Masked under hide-values. */
export function formatPlainINR(paise: number): string {
  const formatted = formatAmountPlainRaw(paise);
  return shouldHideValues() ? maskMoney(formatted) : formatted;
}

/** Format paise as compact INR — 1.2L / 36L / 1.5Cr. Masked under
 * hide-values, same as `formatINR`; see the note there. */
export function formatCompactINR(paise: number): string {
  if (shouldHideValues()) return maskMoney("");
  return formatCompactRaw(paise);
}

/** Format date as DD/MM/YYYY */
export function formatDate(date: Date | string): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Convert rupees to paise for storage */
export function toPaise(rupees: number): number {
  return Math.round(rupees * 100);
}

/** Convert paise to rupees for display */
export function toRupees(paise: number): number {
  return paise / 100;
}
